import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import Sidebar from '../components/Sidebar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Euro, 
  Users, 
  Home,
  Search,
  Save,
  Copy,
  Loader2,
  CheckCircle2,
  AlertCircle,
  ArrowRight,
  Trash2,
  Clock,
  Calculator,
  RefreshCw,
  TrendingUp,
  BarChart3,
  Target,
  Award
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Fonction pour convertir un prix en temps estimé
const priceToTime = (price, hourlyRate) => {
  if (!price || !hourlyRate || hourlyRate <= 0) return null;
  const hours = price / hourlyRate;
  const totalMinutes = Math.round(hours * 60);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  if (h === 0) return `${m}min`;
  if (m === 0) return `${h}h`;
  return `${h}h${m.toString().padStart(2, '0')}`;
};

// Fonction pour convertir un temps en prix
const timeToPrice = (hours, minutes, hourlyRate) => {
  const totalHours = hours + (minutes / 60);
  return Math.round(totalHours * hourlyRate * 100) / 100;
};

const PriceRates = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [housekeepers, setHousekeepers] = useState([]);
  const [selectedHousekeeper, setSelectedHousekeeper] = useState(null);
  const [priceRates, setPriceRates] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [modifiedPrices, setModifiedPrices] = useState({});
  const [modifiedTravelerPrices, setModifiedTravelerPrices] = useState({});
  const [showCopyDialog, setShowCopyDialog] = useState(false);
  const [targetHousekeeper, setTargetHousekeeper] = useState('');
  const [summary, setSummary] = useState([]);
  const [caStats, setCaStats] = useState(null);
  const [activeTab, setActiveTab] = useState('tarifs'); // 'tarifs' ou 'ca'
  
  // FDM de référence pour le prix voyageur (le premier de la liste, ou celui qui contient "CORNUEY")
  const [referenceFdmId, setReferenceFdmId] = useState(null);
  
  // Tarif horaire de référence
  const [hourlyRate, setHourlyRate] = useState(32);
  const [showTimeCalculator, setShowTimeCalculator] = useState(false);
  const [calcHours, setCalcHours] = useState(1);
  const [calcMinutes, setCalcMinutes] = useState(0);

  // Fetch housekeepers
  const fetchHousekeepers = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/users?assignable=true`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      // Filter to only get housekeepers
      const hkList = data.filter(u => u.role === 'housekeeper' || u.is_also_housekeeper);
      setHousekeepers(hkList);
      
      // Identifier le FDM de référence pour le prix voyageur
      // Priorité : celui qui contient "CORNUEY" dans le nom, sinon le premier
      const cornuey = hkList.find(h => h.name?.toUpperCase().includes('CORNUEY'));
      if (cornuey) {
        setReferenceFdmId(cornuey.id);
      } else if (hkList.length > 0) {
        setReferenceFdmId(hkList[0].id);
      }
    } catch (error) {
      console.error('Error fetching housekeepers:', error);
      toast.error('Erreur lors du chargement des femmes de ménage');
    }
  }, [token]);

  // Fetch summary
  const fetchSummary = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/price-rates/summary`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSummary(data);
      }
    } catch (error) {
      console.error('Error fetching summary:', error);
    }
  }, [token]);

  // Fetch CA stats
  const fetchCaStats = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/price-rates/ca-stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setCaStats(data);
      }
    } catch (error) {
      console.error('Error fetching CA stats:', error);
    }
  }, [token]);

  // Fetch price rates for selected housekeeper
  const fetchPriceRates = useCallback(async (housekeeperId) => {
    if (!housekeeperId) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/price-rates/housekeeper/${housekeeperId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setPriceRates(data);
        setModifiedPrices({});
      } else {
        toast.error('Erreur lors du chargement des tarifs');
      }
    } catch (error) {
      console.error('Error fetching price rates:', error);
      toast.error('Erreur lors du chargement des tarifs');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchHousekeepers();
    fetchSummary();
    fetchCaStats();
  }, [fetchHousekeepers, fetchSummary, fetchCaStats]);

  useEffect(() => {
    if (selectedHousekeeper) {
      fetchPriceRates(selectedHousekeeper.id);
    }
  }, [selectedHousekeeper, fetchPriceRates]);

  // Handle price change
  const handlePriceChange = (propertyId, value) => {
    setModifiedPrices(prev => ({
      ...prev,
      [propertyId]: value === '' ? '' : parseFloat(value) || 0
    }));
  };

  // Handle traveler price change
  const handleTravelerPriceChange = (propertyId, value) => {
    setModifiedTravelerPrices(prev => ({
      ...prev,
      [propertyId]: value === '' ? '' : parseFloat(value) || 0
    }));
  };

  // Save traveler price for a property
  const saveTravelerPrice = async (propertyId, travelerPrice) => {
    try {
      // Get current property data first
      const getRes = await fetch(`${API_URL}/api/properties/${propertyId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!getRes.ok) return;
      const property = await getRes.json();
      
      // Update with new traveler price
      const response = await fetch(`${API_URL}/api/properties/${propertyId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...property,
          traveler_price: travelerPrice
        })
      });

      if (response.ok) {
        toast.success('Prix voyageur mis à jour');
        // Remove from modified list
        setModifiedTravelerPrices(prev => {
          const newState = { ...prev };
          delete newState[propertyId];
          return newState;
        });
        // Refresh data
        if (selectedHousekeeper) {
          fetchPriceRates(selectedHousekeeper.id);
        }
        fetchCaStats();
      }
    } catch (error) {
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  // Save all modified prices
  const handleSaveAll = async () => {
    if (!selectedHousekeeper || Object.keys(modifiedPrices).length === 0) return;
    
    setSaving(true);
    try {
      const rates = Object.entries(modifiedPrices).map(([propertyId, price]) => ({
        housekeeper_id: selectedHousekeeper.id,
        property_id: propertyId,
        price: price
      }));

      const response = await fetch(`${API_URL}/api/price-rates/bulk-update`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ rates })
      });

      if (response.ok) {
        toast.success(`${rates.length} tarif(s) mis à jour`);
        setModifiedPrices({});
        fetchPriceRates(selectedHousekeeper.id);
        fetchSummary();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la sauvegarde');
      }
    } catch (error) {
      console.error('Error saving prices:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  // Save single price
  const handleSaveSingle = async (propertyId, price) => {
    if (!selectedHousekeeper) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/price-rates/housekeeper/${selectedHousekeeper.id}/property/${propertyId}`,
        {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ price })
        }
      );

      if (response.ok) {
        toast.success('Tarif mis à jour');
        // Update local state
        setPriceRates(prev => prev.map(r => 
          r.property_id === propertyId 
            ? { ...r, custom_price: price, has_custom_price: true }
            : r
        ));
        setModifiedPrices(prev => {
          const newPrices = { ...prev };
          delete newPrices[propertyId];
          return newPrices;
        });
        fetchSummary();
      } else {
        toast.error('Erreur lors de la sauvegarde');
      }
    } catch (error) {
      console.error('Error saving price:', error);
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  // Delete custom price
  const handleDeletePrice = async (propertyId) => {
    if (!selectedHousekeeper) return;
    
    try {
      const response = await fetch(
        `${API_URL}/api/price-rates/housekeeper/${selectedHousekeeper.id}/property/${propertyId}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${token}` }
        }
      );

      if (response.ok) {
        toast.success('Tarif personnalisé supprimé');
        fetchPriceRates(selectedHousekeeper.id);
        fetchSummary();
      } else {
        toast.error('Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('Error deleting price:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  // Copy rates to another housekeeper
  const handleCopyRates = async () => {
    if (!selectedHousekeeper || !targetHousekeeper) return;
    
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/price-rates/copy`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          source_housekeeper_id: selectedHousekeeper.id,
          target_housekeeper_id: targetHousekeeper
        })
      });

      if (response.ok) {
        const data = await response.json();
        toast.success(data.message);
        setShowCopyDialog(false);
        setTargetHousekeeper('');
        fetchSummary();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Erreur lors de la copie');
      }
    } catch (error) {
      console.error('Error copying rates:', error);
      toast.error('Erreur lors de la copie');
    } finally {
      setSaving(false);
    }
  };

  // Filter rates by search term
  const filteredRates = priceRates.filter(rate =>
    rate.property_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    rate.property_city?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Count modified and custom prices
  const modifiedCount = Object.keys(modifiedPrices).length;
  const customCount = priceRates.filter(r => r.has_custom_price).length;

  return (
    <div className="min-h-screen bg-slate-100">
      <Sidebar />
      
      <main className="flex-1 md:ml-72 overflow-auto">
        <div className="p-6 max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3">
                <Euro className="h-7 w-7 text-[#1F3A8A]" />
                Gestion des Tarifs & CA
              </h1>
              <p className="text-slate-500 mt-1">
                Tarifs personnalisés et chiffre d'affaires de la conciergerie
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="outline"
                onClick={async () => {
                  try {
                    const response = await fetch(`${API_URL}/api/price-rates/force-sync`, {
                      method: 'POST',
                      headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (response.ok) {
                      const data = await response.json();
                      if (data.updated_count > 0) {
                        toast.success(`${data.updated_count} mission(s) mise(s) à jour`);
                      } else {
                        toast.info('Tous les prix sont déjà synchronisés');
                      }
                    } else {
                      toast.error('Erreur lors de la synchronisation');
                    }
                  } catch (error) {
                    toast.error('Erreur lors de la synchronisation');
                  }
                }}
                className="gap-2"
                data-testid="sync-prices-btn"
              >
                <RefreshCw className="h-4 w-4" />
                Synchroniser les prix
              </Button>
              <Button
                variant="outline"
                className="gap-2 border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                onClick={async () => {
                  try {
                    const response = await fetch(`${API_URL}/api/admin/recompute-linen-splits`, {
                      method: 'POST',
                      headers: { 'Authorization': `Bearer ${token}` }
                    });
                    if (response.ok) {
                      const data = await response.json();
                      if (data.payments_updated > 0) {
                        toast.success(`👕 ${data.payments_updated} paiement(s) recalculé(s) sur ${data.tasks_changed} prestation(s)`);
                      } else {
                        toast.info(`Tous les paiements sont déjà à jour (${data.tasks_processed} prestation(s) analysée(s))`);
                      }
                    } else {
                      toast.error('Erreur lors du recalcul');
                    }
                  } catch (error) {
                    toast.error('Erreur lors du recalcul');
                  }
                }}
                data-testid="recompute-linen-btn"
              >
                <RefreshCw className="h-4 w-4" />
                👕 Recalculer répartition linge
              </Button>
            </div>
          </div>

          {/* CA Statistics Cards */}
          {caStats && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              {/* CA Mois */}
              <Card className="border-0 shadow-md bg-gradient-to-br from-emerald-500 to-emerald-600 text-white">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-emerald-100 text-sm font-medium">CA du mois</p>
                      <p className="text-3xl font-bold mt-1">{caStats.current_month.margin.toLocaleString('fr-FR')}€</p>
                      <p className="text-emerald-200 text-xs mt-1">
                        {caStats.current_month.count} ménages • {caStats.current_month.margin_percent}% de marge
                      </p>
                    </div>
                    <div className="bg-white/20 rounded-full p-3">
                      <TrendingUp className="h-8 w-8" />
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-emerald-400/30 grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <p className="text-emerald-200">Facturé voyageurs</p>
                      <p className="font-semibold">{caStats.current_month.total_voyageur.toLocaleString('fr-FR')}€</p>
                    </div>
                    <div>
                      <p className="text-emerald-200">Payé FDM</p>
                      <p className="font-semibold">{caStats.current_month.total_fdm.toLocaleString('fr-FR')}€</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* CA 6 mois */}
              <Card className="border-0 shadow-md bg-gradient-to-br from-blue-500 to-blue-600 text-white">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-blue-100 text-sm font-medium">CA 6 derniers mois</p>
                      <p className="text-3xl font-bold mt-1">{caStats.last_6_months.margin.toLocaleString('fr-FR')}€</p>
                      <p className="text-blue-200 text-xs mt-1">
                        {caStats.last_6_months.count} ménages • {caStats.last_6_months.margin_percent}% de marge
                      </p>
                    </div>
                    <div className="bg-white/20 rounded-full p-3">
                      <BarChart3 className="h-8 w-8" />
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-blue-400/30 grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <p className="text-blue-200">Facturé voyageurs</p>
                      <p className="font-semibold">{caStats.last_6_months.total_voyageur.toLocaleString('fr-FR')}€</p>
                    </div>
                    <div>
                      <p className="text-blue-200">Payé FDM</p>
                      <p className="font-semibold">{caStats.last_6_months.total_fdm.toLocaleString('fr-FR')}€</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* CA Année */}
              <Card className="border-0 shadow-md bg-gradient-to-br from-purple-500 to-purple-600 text-white">
                <CardContent className="p-5">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-purple-100 text-sm font-medium">CA {caStats.current_year.label}</p>
                      <p className="text-3xl font-bold mt-1">{caStats.current_year.margin.toLocaleString('fr-FR')}€</p>
                      <p className="text-purple-200 text-xs mt-1">
                        {caStats.current_year.count} ménages • {caStats.current_year.margin_percent}% de marge
                      </p>
                    </div>
                    <div className="bg-white/20 rounded-full p-3">
                      <Target className="h-8 w-8" />
                    </div>
                  </div>
                  <div className="mt-3 pt-3 border-t border-purple-400/30 grid grid-cols-2 gap-2 text-sm">
                    <div>
                      <p className="text-purple-200">Facturé voyageurs</p>
                      <p className="font-semibold">{caStats.current_year.total_voyageur.toLocaleString('fr-FR')}€</p>
                    </div>
                    <div>
                      <p className="text-purple-200">Payé FDM</p>
                      <p className="font-semibold">{caStats.current_year.total_fdm.toLocaleString('fr-FR')}€</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* CA Prévisionnel - Tâches assignées non terminées */}
          {caStats && caStats.forecast && (
            <Card className="border-0 shadow-md bg-gradient-to-br from-amber-500 to-orange-500 text-white mb-6">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <p className="text-amber-100 text-sm font-medium">📊 CA Prévisionnel (3 prochains mois)</p>
                    <p className="text-3xl font-bold mt-1">{caStats.forecast.margin.toLocaleString('fr-FR')}€</p>
                    <p className="text-amber-200 text-xs mt-1">
                      {caStats.forecast.count} ménages planifiés • Marge prévisionnelle: {caStats.forecast.margin_percent}%
                    </p>
                  </div>
                  <div className="bg-white/20 rounded-full p-3">
                    <TrendingUp className="h-8 w-8" />
                  </div>
                </div>
                
                {/* Détail par mois */}
                {caStats.forecast.by_month && caStats.forecast.by_month.length > 0 && (
                  <div className="mt-3 pt-3 border-t border-amber-400/30">
                    <p className="text-amber-200 text-xs mb-2 font-medium">Détail par mois :</p>
                    <div className="grid grid-cols-3 gap-3">
                      {caStats.forecast.by_month.map((month) => (
                        <div key={month.month} className="bg-white/10 rounded-lg p-2 text-center">
                          <p className="text-amber-200 text-xs">{month.month}</p>
                          <p className="font-bold text-lg">{month.margin.toLocaleString('fr-FR')}€</p>
                          <p className="text-amber-200 text-xs">{month.count} ménages</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                <div className="mt-3 pt-3 border-t border-amber-400/30 grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <p className="text-amber-200">À facturer voyageurs</p>
                    <p className="font-semibold">{caStats.forecast.total_voyageur.toLocaleString('fr-FR')}€</p>
                  </div>
                  <div>
                    <p className="text-amber-200">À payer FDM</p>
                    <p className="font-semibold">{caStats.forecast.total_fdm.toLocaleString('fr-FR')}€</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Top Logements par CA */}
          {caStats && caStats.top_properties && caStats.top_properties.length > 0 && (
            <Card className="border-0 shadow-sm mb-6">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Award className="h-5 w-5 text-amber-500" />
                  Top 5 Logements par CA
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                  {caStats.top_properties.map((prop, idx) => (
                    <div 
                      key={prop.property_id}
                      className={`p-3 rounded-lg border ${idx === 0 ? 'bg-amber-50 border-amber-200' : 'bg-slate-50 border-slate-200'}`}
                    >
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                          idx === 0 ? 'bg-amber-500 text-white' : 
                          idx === 1 ? 'bg-slate-400 text-white' : 
                          idx === 2 ? 'bg-amber-700 text-white' : 
                          'bg-slate-300 text-slate-700'
                        }`}>
                          {idx + 1}
                        </span>
                        <span className="font-medium text-sm truncate">{prop.property_name}</span>
                      </div>
                      <p className="text-lg font-bold text-emerald-600">+{prop.total_margin.toFixed(0)}€</p>
                      <p className="text-xs text-slate-500">{prop.count} ménage(s)</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          {/* Summary Cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {summary.slice(0, 6).map(item => (
              <div 
                key={item.housekeeper_id}
                onClick={() => {
                  const hk = housekeepers.find(h => h.id === item.housekeeper_id);
                  if (hk) setSelectedHousekeeper(hk);
                }}
                className={`bg-white rounded-xl p-4 shadow-sm border cursor-pointer transition-all hover:shadow-md ${
                  selectedHousekeeper?.id === item.housekeeper_id ? 'border-[#1F3A8A] ring-2 ring-[#1F3A8A]/20' : 'border-slate-200'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                    <Users className="h-5 w-5 text-emerald-600" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-slate-800 truncate">{item.housekeeper_name}</p>
                    <p className="text-sm text-slate-500">
                      {item.custom_rates_count} tarif{item.custom_rates_count > 1 ? 's' : ''} personnalisé{item.custom_rates_count > 1 ? 's' : ''}
                    </p>
                  </div>
                  <ArrowRight className="h-5 w-5 text-slate-400" />
                </div>
              </div>
            ))}
          </div>

          {/* Main Content */}
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            {/* Left Panel - Housekeeper Selection */}
            <div className="lg:col-span-1">
              <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4">
                <h3 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  Femmes de ménage
                </h3>
                <div className="space-y-2 max-h-96 overflow-y-auto">
                  {housekeepers.map(hk => (
                    <button
                      key={hk.id}
                      onClick={() => setSelectedHousekeeper(hk)}
                      className={`w-full text-left p-3 rounded-lg transition-all ${
                        selectedHousekeeper?.id === hk.id
                          ? 'bg-[#1F3A8A] text-white'
                          : 'bg-slate-50 hover:bg-slate-100 text-slate-700'
                      }`}
                    >
                      <p className="font-medium truncate">{hk.name}</p>
                      <p className={`text-xs ${selectedHousekeeper?.id === hk.id ? 'text-blue-200' : 'text-slate-500'}`}>
                        {summary.find(s => s.housekeeper_id === hk.id)?.custom_rates_count || 0} tarifs
                      </p>
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Right Panel - Price Rates */}
            <div className="lg:col-span-3">
              {selectedHousekeeper ? (
                <div className="bg-white rounded-xl shadow-sm border border-slate-200">
                  {/* Header */}
                  <div className="p-4 border-b border-slate-200">
                    <div className="flex flex-wrap items-center justify-between gap-4">
                      <div>
                        <h3 className="font-semibold text-slate-800 text-lg">
                          Barème de {selectedHousekeeper.name}
                        </h3>
                        <p className="text-sm text-slate-500">
                          {customCount} tarif{customCount > 1 ? 's' : ''} personnalisé{customCount > 1 ? 's' : ''}
                        </p>
                        {/* Indication FDM de référence pour prix voyageur */}
                        {selectedHousekeeper.id === referenceFdmId ? (
                          <p className="text-xs text-blue-600 mt-1 flex items-center gap-1">
                            <span className="inline-block w-2 h-2 bg-blue-500 rounded-full"></span>
                            FDM de référence — Modifiez ici le "Prix Voyageur" (s'applique à tous)
                          </p>
                        ) : (
                          <p className="text-xs text-slate-400 mt-1">
                            Prix Voyageur en lecture seule (modifiable via {housekeepers.find(h => h.id === referenceFdmId)?.name || 'FDM de référence'})
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        {/* Search */}
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                          <Input
                            type="text"
                            placeholder="Rechercher..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="pl-9 w-48"
                          />
                        </div>
                        
                        {/* Copy Button */}
                        <Button
                          variant="outline"
                          onClick={() => setShowCopyDialog(true)}
                          disabled={customCount === 0}
                          className="gap-2"
                        >
                          <Copy className="h-4 w-4" />
                          Copier vers...
                        </Button>
                        
                        {/* Save All Button */}
                        {modifiedCount > 0 && (
                          <Button
                            onClick={handleSaveAll}
                            disabled={saving}
                            className="bg-[#1F3A8A] hover:bg-[#162d6e] gap-2"
                          >
                            {saving ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Save className="h-4 w-4" />
                            )}
                            Enregistrer ({modifiedCount})
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Table */}
                  {loading ? (
                    <div className="flex items-center justify-center py-12">
                      <Loader2 className="h-8 w-8 animate-spin text-[#1F3A8A]" />
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead className="bg-slate-50">
                          <tr>
                            <th className="text-left p-3 font-medium text-slate-600">Logement</th>
                            <th className="text-left p-3 font-medium text-slate-600">Ville</th>
                            <th className="text-right p-3 font-medium text-slate-600">Prix FDM</th>
                            <th className="text-right p-3 font-medium text-slate-600 bg-blue-50">Prix Voyageur</th>
                            <th className="text-right p-3 font-medium text-emerald-700 bg-emerald-50">Marge</th>
                            <th className="text-center p-3 font-medium text-slate-600">Actions</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100">
                          {filteredRates.map(rate => {
                            const isModified = modifiedPrices.hasOwnProperty(rate.property_id);
                            const isTravelerModified = modifiedTravelerPrices.hasOwnProperty(rate.property_id);
                            
                            // Prix FDM = le tarif personnalisé (ou vide si non défini)
                            const fdmPrice = isModified 
                              ? modifiedPrices[rate.property_id] 
                              : (rate.custom_price ?? '');
                            
                            // Prix voyageur
                            const travelerPrice = isTravelerModified
                              ? modifiedTravelerPrices[rate.property_id]
                              : (rate.traveler_price || '');
                            
                            // Vérifier si c'est le FDM de référence pour le prix voyageur
                            const isReferenceFdm = selectedHousekeeper?.id === referenceFdmId;
                            
                            // Calcul de la marge (seulement si les deux prix sont définis)
                            const fdmPriceNum = fdmPrice !== '' ? parseFloat(fdmPrice) : null;
                            const travelerPriceNum = travelerPrice !== '' ? parseFloat(travelerPrice) : null;
                            const canCalculateMargin = fdmPriceNum !== null && travelerPriceNum !== null && travelerPriceNum > 0;
                            const margin = canCalculateMargin ? travelerPriceNum - fdmPriceNum : null;
                            const marginPercent = canCalculateMargin ? (margin / travelerPriceNum * 100).toFixed(0) : null;
                            
                            return (
                              <tr 
                                key={rate.property_id}
                                className={`hover:bg-slate-50 ${isModified || isTravelerModified ? 'bg-amber-50' : ''}`}
                              >
                                <td className="p-3">
                                  <div className="flex items-center gap-2">
                                    <Home className="h-4 w-4 text-slate-400" />
                                    <span className="font-medium text-slate-700">{rate.property_name}</span>
                                  </div>
                                </td>
                                <td className="p-3 text-slate-600">{rate.property_city || '-'}</td>
                                <td className="p-3">
                                  <div className="flex items-center justify-end gap-2">
                                    <Input
                                      type="number"
                                      step="0.5"
                                      min="0"
                                      value={fdmPrice}
                                      onChange={(e) => handlePriceChange(rate.property_id, e.target.value)}
                                      placeholder="—"
                                      className={`w-20 text-right ${rate.has_custom_price && !isModified ? 'border-emerald-300 bg-emerald-50' : ''}`}
                                    />
                                    <span className="text-slate-500">€</span>
                                  </div>
                                </td>
                                <td className="p-3 bg-blue-50/50">
                                  <div className="flex items-center justify-end gap-2">
                                    {isReferenceFdm ? (
                                      // FDM de référence : champ modifiable
                                      <Input
                                        type="number"
                                        step="1"
                                        min="0"
                                        value={travelerPrice}
                                        onChange={(e) => handleTravelerPriceChange(rate.property_id, e.target.value)}
                                        placeholder="—"
                                        className="w-20 text-right border-blue-200"
                                      />
                                    ) : (
                                      // Autres FDM : affichage lecture seule
                                      <span className="w-20 text-right font-medium text-blue-600">
                                        {travelerPrice || '—'}
                                      </span>
                                    )}
                                    <span className="text-slate-500">€</span>
                                  </div>
                                </td>
                                <td className={`p-3 text-right font-bold bg-emerald-50/50 ${
                                  margin !== null 
                                    ? (margin > 0 ? 'text-emerald-600' : margin < 0 ? 'text-red-600' : 'text-slate-400')
                                    : 'text-slate-300'
                                }`}>
                                  {margin !== null ? (
                                    <>
                                      {margin > 0 ? '+' : ''}{margin.toFixed(0)}€
                                      <span className="text-xs font-normal ml-1">({marginPercent}%)</span>
                                    </>
                                  ) : (
                                    '—'
                                  )}
                                </td>
                                <td className="p-3">
                                  <div className="flex items-center justify-center gap-1">
                                    {(isModified || (isTravelerModified && isReferenceFdm)) && (
                                      <Button
                                        size="sm"
                                        onClick={async () => {
                                          // Sauvegarder les deux prix si modifiés
                                          if (isModified) {
                                            await handleSaveSingle(rate.property_id, modifiedPrices[rate.property_id]);
                                          }
                                          if (isTravelerModified && isReferenceFdm) {
                                            await saveTravelerPrice(rate.property_id, modifiedTravelerPrices[rate.property_id]);
                                          }
                                        }}
                                        className="bg-emerald-600 hover:bg-emerald-700 h-8 px-3"
                                      >
                                        <CheckCircle2 className="h-4 w-4 mr-1" />
                                        Enregistrer
                                      </Button>
                                    )}
                                    {rate.has_custom_price && !isModified && !(isTravelerModified && isReferenceFdm) && (
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => handleDeletePrice(rate.property_id)}
                                        className="text-red-500 hover:text-red-700 hover:bg-red-50 h-8 w-8 p-0"
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                      
                      {filteredRates.length === 0 && (
                        <div className="text-center py-12 text-slate-500">
                          <AlertCircle className="h-12 w-12 mx-auto mb-3 opacity-50" />
                          <p>Aucun logement trouvé</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
                  <Users className="h-16 w-16 mx-auto text-slate-300 mb-4" />
                  <h3 className="text-lg font-semibold text-slate-600 mb-2">
                    Sélectionnez une femme de ménage
                  </h3>
                  <p className="text-slate-500">
                    Choisissez une personne dans la liste pour voir et modifier ses tarifs
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Copy Dialog */}
      <Dialog open={showCopyDialog} onOpenChange={setShowCopyDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Copy className="h-5 w-5 text-[#1F3A8A]" />
              Copier les tarifs
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <p className="text-sm text-slate-600">
              Copier tous les tarifs de <strong>{selectedHousekeeper?.name}</strong> vers :
            </p>
            <div className="space-y-2">
              <Label>Femme de ménage cible</Label>
              <Select value={targetHousekeeper} onValueChange={setTargetHousekeeper}>
                <SelectTrigger>
                  <SelectValue placeholder="Sélectionner..." />
                </SelectTrigger>
                <SelectContent>
                  {housekeepers
                    .filter(hk => hk.id !== selectedHousekeeper?.id)
                    .map(hk => (
                      <SelectItem key={hk.id} value={hk.id}>
                        {hk.name}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="text-sm text-amber-700">
                <strong>Attention :</strong> Cette action remplacera les tarifs existants de la personne cible.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCopyDialog(false)}>
              Annuler
            </Button>
            <Button
              onClick={handleCopyRates}
              disabled={!targetHousekeeper || saving}
              className="bg-[#1F3A8A] hover:bg-[#162d6e]"
            >
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Copy className="h-4 w-4 mr-2" />
              )}
              Copier les tarifs
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PriceRates;
